/**
 * Conveniences remembered in this browser: theme, collapsed menu, columns and
 * density of the grids. Never data: storage can be missing or cleared (private
 * windows), so every read falls back to a default.
 */
import { useCallback, useState } from 'react';

const PREFIX = 'k-management.';

export function readPreference<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writePreference(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage unavailable: the choice lasts until the page is reloaded.
  }
}

/** Removes what this browser keeps under the keys starting with one of `prefixes`. */
export function forgetPreferences(prefixes: readonly string[]): void {
  try {
    const storage = window.localStorage;
    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index));
    for (const key of keys) {
      if (key && prefixes.some((prefix) => key.startsWith(PREFIX + prefix))) {
        storage.removeItem(key);
      }
    }
  } catch {
    // Storage unavailable: nothing kept to remove.
  }
}

/** A value kept in this browser under `key`, like useState. */
export function usePreference<T>(key: string, fallback: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => readPreference(key, fallback));
  const update = useCallback(
    (next: T) => {
      setValue(next);
      writePreference(key, next);
    },
    [key],
  );
  return [value, update];
}

/** Light, dark or as the operating system says. */
export type Theme = 'light' | 'dark' | 'system';

export const THEME_KEY = 'theme';

export function applyTheme(theme: Theme): void {
  if (theme === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
}

/** What useSessionValue keeps under `key`, read once (the exports read the sort of the page). */
export function readSessionValue<T>(key: string, fallback: T): T {
  try {
    const raw = window.sessionStorage.getItem(PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

/**
 * A value kept while this browser tab stays open (11/10/2026): the filters,
 * search and page of a list, found again when coming back to it, gone the
 * next day so that a forgotten filter does not hide data.
 */
export function useSessionValue<T>(key: string, fallback: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => readSessionValue(key, fallback));
  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.sessionStorage.setItem(PREFIX + key, JSON.stringify(next));
      } catch {
        // Storage unavailable: the value lasts while the page stays open.
      }
    },
    [key],
  );
  return [value, update];
}
